import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Camera as CameraIcon,
  Sparkles,
  Hand,
  Check,
  RotateCcw,
  Shirt,
  User,
  Zap,
  X,
  ClipboardPaste,
  Images,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Button, Spinner, inputClass, Field } from '@/components/ui';
import { ComboSelect } from '@/features/wardrobe/components/ComboSelect';
import { SizeField } from '@/features/wardrobe/components/SizeField';
import { downscaleToBase64, remoteImageToBase64 } from '@/lib/image';
import { bestStoreForDesigner } from '@/lib/match';
import { todayISO, cn } from '@/lib/utils';
import { SLOT_CATEGORIES, toStoredSizeType, formatStoredSize } from '@/lib/constants';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import type { AiItemAnalysis, AiFieldResult, Designer, FitSlot, Item, SizeSystem, Store } from '@/lib/types';

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  brand: 'Designer',
  category: 'Category',
  color: 'Color',
  material: 'Material',
  season: 'Season',
};

// What we hand off to the Add Item form so AI details / the photo aren't lost.
export interface CameraPrefill {
  name?: string;
  brand?: string;
  category?: string;
  color?: string;
  material?: string;
  season?: string;
  storeName?: string;
  storeId?: string;
  sizeSystem?: SizeSystem;
  size?: string;
  isBespoke?: boolean;
  bespokeDate?: string;
}

type Stage =
  | 'capture'
  | 'detecting'
  | 'choose-type'
  | 'choose-mode'
  | 'ai-processing'
  | 'item-results'
  | 'fit-results'
  | 'fit-logging'
  | 'fit-success';

type CaptureType = 'item' | 'fit';

interface DetectResponse {
  detectedType: 'item' | 'fit' | 'not_clothing';
  confidence: number;
}

interface MatchCandidate {
  itemId: string;
  confidence: number;
}

interface MatchedGarment {
  description: string;
  matchedItemId: string | null;
  confidence: number;
  candidates: MatchCandidate[];
}

interface MatchFitResponse {
  garments: MatchedGarment[];
}

// Confidence badge color: green >=0.85, amber 0.7-0.84, red <0.7
function confidenceClass(confidence: number): string {
  if (confidence >= 0.85) return 'text-green-600 bg-green-50';
  if (confidence >= 0.7) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
}

function slotForCategory(category: string): FitSlot {
  for (const [slot, cats] of Object.entries(SLOT_CATEGORIES)) {
    if (cats.includes(category)) return slot as FitSlot;
  }
  return 'accessory';
}

const ITEM_FIELDS: (keyof AiItemAnalysis)[] = ['name', 'brand', 'category', 'color', 'material', 'season'];

export function CameraPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: designerData } = useApi<{ designers: Designer[] }>('/designers');
  const designerNames = (designerData?.designers ?? []).map((d) => d.name);
  const { data: storeData } = useApi<{ stores: Store[] }>('/stores');
  const stores = storeData?.stores ?? [];

  // Rapid Intake survives a round-trip through the full Add Item form: the form
  // navigates back here with the flag + running count so the session continues.
  const resumed = location.state as { rapidIntake?: boolean; intakeCount?: number } | null;

  const [stage, setStage] = useState<Stage>('capture');
  const [image, setImage] = useState<string | null>(null);
  const [rapidIntake, setRapidIntake] = useState(resumed?.rapidIntake ?? false);
  const [intakeCount, setIntakeCount] = useState(resumed?.intakeCount ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [pasteHint, setPasteHint] = useState<string | null>(null);

  const [detectedType, setDetectedType] = useState<CaptureType>('item');
  const [selectedType, setSelectedType] = useState<CaptureType>('item');

  // Item AI results — editable field values + selected alternatives
  const [analysis, setAnalysis] = useState<AiItemAnalysis | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [savingItem, setSavingItem] = useState(false);

  // Store + size aren't AI-detected, but surfacing them here saves a trip to
  // the full form. Store defaults from the detected designer.
  const [storeName, setStoreName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [sizeSystem, setSizeSystem] = useState<SizeSystem>('relative');
  const [size, setSize] = useState('');
  const [isBespoke, setIsBespoke] = useState(false);
  const [bespokeDate, setBespokeDate] = useState('');
  const storeNames = stores.map((s) => s.name);

  // Fit matching
  const [garments, setGarments] = useState<MatchedGarment[]>([]);
  const [itemsById, setItemsById] = useState<Record<string, Item>>({});
  const [fitName, setFitName] = useState('');
  const [existingFit, setExistingFit] = useState<{ id: string; name: string } | null>(null);
  const [loggingFit, setLoggingFit] = useState(false);

  const resetAll = () => {
    setStage('capture');
    setImage(null);
    setError(null);
    setAnalysis(null);
    setFieldValues({});
    setStoreName('');
    setStoreId('');
    setSizeSystem('relative');
    setSize('');
    setIsBespoke(false);
    setBespokeDate('');
    setGarments([]);
    setItemsById({});
    setFitName('');
    setExistingFit(null);
  };

  // Run a captured/pasted base64 image through detection.
  const processImage = async (base64: string) => {
    setError(null);
    setImage(base64);
    setStage('detecting');
    try {
      const res = await api.post<DetectResponse>('/ai/detect', { image: base64 });
      if (res.detectedType === 'not_clothing') {
        setError('That does not look like clothing. Try another photo.');
        setStage('capture');
        setImage(null);
        return;
      }
      const detected: CaptureType = res.detectedType === 'fit' ? 'fit' : 'item';
      setDetectedType(detected);
      setSelectedType(detected);
      setStage('choose-type');
    } catch {
      // Fall back to manual type choice
      setDetectedType('item');
      setSelectedType('item');
      setStage('choose-type');
    }
  };

  const handleCapture = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    try {
      await processImage(await downscaleToBase64(file));
    } catch {
      setError('Could not read that photo. Please try again.');
    }
  };

  // Bring an image in from the clipboard (Copy Image on any site → paste here).
  const ingestPastedBlob = async (blob: Blob) => {
    try {
      await processImage(await downscaleToBase64(blob));
    } catch {
      setError('Could not read that image.');
    }
  };

  const pasteFromClipboardButton = async () => {
    setPasteHint(null);
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          await ingestPastedBlob(blob);
          return;
        }
      }
      setPasteHint('No image in clipboard — copy an image first, then paste.');
    } catch {
      setPasteHint('Press Ctrl/Cmd+V to paste a copied image.');
    }
  };

  // Global paste while on the capture screen: grab a copied image (or image URL).
  useEffect(() => {
    if (stage !== 'capture') return;
    const onPaste = (e: ClipboardEvent) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const imgItem = Array.from(cd.items).find((i) => i.type.startsWith('image/'));
      if (imgItem) {
        const file = imgItem.getAsFile();
        if (file) {
          e.preventDefault();
          void ingestPastedBlob(file);
          return;
        }
      }
      const text = cd.getData('text/plain').trim();
      if (/^https?:\/\/\S+/i.test(text)) {
        e.preventDefault();
        void remoteImageToBase64(text).then((b) => {
          if (b) void processImage(b);
          else setPasteHint("Couldn't read that image URL.");
        });
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleConfirmType = (type: CaptureType) => {
    setSelectedType(type);
    setStage('choose-mode');
  };

  // Hand the captured photo (and any AI details) to the Add Item form via
  // router state so nothing is lost when the user edits manually.
  const goToAddItem = (prefill?: CameraPrefill) => {
    const enriched: CameraPrefill = { ...prefill };
    // Prefer the store set here; otherwise derive it from the designer.
    if (storeName.trim()) {
      enriched.storeName = storeName.trim();
      enriched.storeId = storeId || undefined;
    } else if (prefill?.brand) {
      const store = bestStoreForDesigner(stores, prefill.brand);
      if (store) {
        enriched.storeName = store.name;
        enriched.storeId = store.id;
      }
    }
    // Carry the size chosen here so it isn't lost on the full form.
    if (isBespoke) {
      enriched.isBespoke = true;
      enriched.bespokeDate = bespokeDate || undefined;
    } else if (size) {
      enriched.sizeSystem = sizeSystem;
      enriched.size = size;
    }
    const photoKind: 'item' | 'wear' = detectedType === 'fit' ? 'wear' : 'item';
    navigate('/wardrobe/add', {
      state: { photo: image, photoKind, rapidIntake, intakeCount, prefill: enriched },
    });
  };

  const handleChooseMode = async (mode: 'ai' | 'manual') => {
    if (mode === 'manual') {
      if (selectedType === 'fit') navigate('/fits/add');
      else goToAddItem();
      return;
    }
    if (!image) return;
    setStage('ai-processing');
    setError(null);
    if (selectedType === 'item') {
      try {
        const res = await api.post<{ analysis: AiItemAnalysis }>('/ai/analyze-item', { image });
        setAnalysis(res.analysis);
        const values: Record<string, string> = {};
        for (const key of ITEM_FIELDS) {
          values[key] = (res.analysis[key] as AiFieldResult)?.value ?? '';
        }
        setFieldValues(values);
        // Seed the store from the detected designer (same as the manual form).
        const detectedBrand = res.analysis.brand?.value?.trim();
        const store = detectedBrand ? bestStoreForDesigner(stores, detectedBrand) : null;
        setStoreName(store?.name ?? '');
        setStoreId(store?.id ?? '');
        setStage('item-results');
      } catch {
        setError('AI analysis failed. You can enter details manually.');
        setStage('choose-mode');
      }
    } else {
      try {
        const res = await api.post<MatchFitResponse>('/ai/match-fit', { image });
        setGarments(res.garments);
        await loadItemNames();
        setStage('fit-results');
      } catch {
        setError('Could not match your outfit. Try manual entry.');
        setStage('choose-mode');
      }
    }
  };

  const loadItemNames = async () => {
    try {
      const res = await api.get<{ items: Item[] }>('/items');
      const map: Record<string, Item> = {};
      for (const it of res.items) map[it.id] = it;
      setItemsById(map);
    } catch {
      /* names are best-effort */
    }
  };

  // Resolve the store field to an id, creating a new store if one was typed.
  // Mirrors resolveStore in the manual Add Item form.
  const resolveStore = async (): Promise<{ storeId?: string; storeName?: string }> => {
    const trimmed = storeName.trim();
    if (!trimmed) return {};
    const matched = stores.find((s) => s.id === storeId && s.name === trimmed)
      ?? stores.find((s) => s.name.toLowerCase() === trimmed.toLowerCase());
    if (matched) return { storeId: matched.id, storeName: matched.name };
    try {
      const res = await api.post<{ store: Store }>('/stores', { name: trimmed });
      if (res?.store?.id) return { storeId: res.store.id, storeName: res.store.name };
    } catch {
      /* best-effort; still snapshot the name */
    }
    return { storeName: trimmed };
  };

  // Clear the per-item fields so the next Rapid Intake capture starts fresh.
  const resetForNextCapture = () => {
    setImage(null);
    setAnalysis(null);
    setFieldValues({});
    setStoreName('');
    setStoreId('');
    setSizeSystem('relative');
    setSize('');
    setIsBespoke(false);
    setBespokeDate('');
    setStage('capture');
  };

  // --- Item save (rapid intake aware) ---
  const handleAddItem = async () => {
    if (!image || !analysis) return;
    setSavingItem(true);
    setError(null);
    const material = fieldValues.material?.trim();
    const season = fieldValues.season?.trim();
    const brand = fieldValues.brand?.trim();
    const store = await resolveStore();
    const hasSize = !isBespoke && !!size;
    const photoKind: 'item' | 'wear' = detectedType === 'fit' ? 'wear' : 'item';
    const body = {
      name: fieldValues.name?.trim() || 'New Item',
      category: fieldValues.category?.trim() || 'Accessories',
      brand: brand || undefined,
      storeId: store.storeId,
      storeName: store.storeName,
      color: fieldValues.color?.trim() || undefined,
      materials: material ? [material] : [],
      seasons: season
        ? season
            .split(/[,/]/)
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
        : [],
      sizeType: hasSize ? toStoredSizeType(sizeSystem) : undefined,
      size: hasSize ? formatStoredSize(sizeSystem, size) : undefined,
      isBespoke: isBespoke || undefined,
      bespokeDate: isBespoke ? bespokeDate || undefined : undefined,
      status: 'in-closet' as const,
      photos: [{ image, kind: photoKind }],
    };
    try {
      await api.post<{ item: { id: string } }>('/items', body);
      if (rapidIntake) {
        setIntakeCount((c) => c + 1);
        resetForNextCapture();
      } else {
        navigate('/wardrobe/items');
      }
    } catch {
      setError('Failed to save item. Please try again.');
    } finally {
      setSavingItem(false);
    }
  };

  // --- Fit logging ---
  const matchedItemIds = garments
    .map((g) => g.matchedItemId)
    .filter((id): id is string => id !== null);

  const startLogFit = async () => {
    if (matchedItemIds.length === 0) return;
    setError(null);
    setLoggingFit(true);
    try {
      const res = await api.get<{ fit: { id: string; name: string } | null }>(
        `/fits/find-by-items?ids=${matchedItemIds.join(',')}`,
      );
      if (res.fit) {
        setExistingFit(res.fit);
        await logWearForExistingFit(res.fit.id);
      } else {
        setExistingFit(null);
        setStage('fit-logging');
      }
    } catch {
      setError('Could not check for an existing fit. Please try again.');
    } finally {
      setLoggingFit(false);
    }
  };

  const attachPhotoToWear = async (wearId: string) => {
    if (!image) return;
    try {
      await api.post('/photos', { ownerType: 'fit_wear', ownerId: wearId, image });
    } catch {
      /* best effort */
    }
  };

  const logWearForExistingFit = async (fitId: string) => {
    setLoggingFit(true);
    try {
      const res = await api.post<{ wear?: { id: string } }>(`/fits/${fitId}/wears`, {
        wornDate: todayISO(),
      });
      if (res.wear?.id) await attachPhotoToWear(res.wear.id);
      setStage('fit-success');
    } catch {
      setError('Failed to log wear. Please try again.');
    } finally {
      setLoggingFit(false);
    }
  };

  const createFitAndLog = async () => {
    if (!fitName.trim() || matchedItemIds.length === 0) return;
    setLoggingFit(true);
    setError(null);
    const pieces = matchedItemIds.map((itemId) => {
      const item = itemsById[itemId];
      return { itemId, slot: item ? slotForCategory(item.category) : ('accessory' as FitSlot) };
    });
    try {
      const res = await api.post<{ fit: { id: string }; wear?: { id: string } }>('/fits', {
        name: fitName.trim(),
        pieces,
        logWear: { wornDate: todayISO() },
      });
      if (res.wear?.id) await attachPhotoToWear(res.wear.id);
      setStage('fit-success');
    } catch {
      setError('Failed to create fit. Please try again.');
    } finally {
      setLoggingFit(false);
    }
  };

  const toggleGarmentMatch = (idx: number, candidateId: string) => {
    setGarments((prev) =>
      prev.map((g, i) => {
        if (i !== idx) return g;
        const cand = g.candidates.find((c) => c.itemId === candidateId);
        return {
          ...g,
          matchedItemId: g.matchedItemId === candidateId ? null : candidateId,
          confidence: cand ? cand.confidence : g.confidence,
        };
      }),
    );
  };

  return (
    <Page>
      <PageHeader section="Camera" />

      <div className="max-w-lg mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 rounded-xl bg-destructive/10 text-destructive px-4 py-3 text-sm">{error}</div>
        )}

        {/* CAPTURE */}
        {stage === 'capture' && (
          <div className="space-y-6">
            {/* Rapid intake toggle */}
            <button
              type="button"
              onClick={() => setRapidIntake((v) => !v)}
              className={cn(
                'w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all',
                rapidIntake ? 'border-primary bg-primary/5' : 'border-border bg-card',
              )}
            >
              <div className="flex items-center gap-3 text-left">
                <div className={cn('p-2 rounded-xl', rapidIntake ? 'bg-primary/10' : 'bg-secondary')}>
                  <Zap className={cn('w-5 h-5', rapidIntake ? 'text-primary' : 'text-muted-foreground')} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Rapid Intake</p>
                  <p className="text-xs text-muted-foreground">Add many items back-to-back</p>
                </div>
              </div>
              <div
                className={cn(
                  'relative w-12 h-7 rounded-full transition-colors',
                  rapidIntake ? 'bg-primary' : 'bg-border',
                )}
              >
                <div
                  className={cn(
                    'absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-all',
                    rapidIntake ? 'left-6' : 'left-1',
                  )}
                />
              </div>
            </button>

            {rapidIntake && intakeCount > 0 && (
              <div className="flex items-center justify-between bg-secondary/50 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-foreground">{intakeCount} items added</span>
                <Button variant="outline" className="py-2 px-3" onClick={() => navigate('/wardrobe/items')}>
                  Done
                </Button>
              </div>
            )}

            <label className="block cursor-pointer">
              <div className="aspect-[3/4] bg-foreground/5 rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center hover:border-primary/40 transition-colors">
                <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-4">
                  <CameraIcon className="w-10 h-10 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground text-center px-8">
                  Tap to take a photo of a clothing item or your full outfit
                </p>
              </div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCapture}
                className="hidden"
              />
            </label>

            <label className="block cursor-pointer">
              <div className="w-full py-4 bg-primary text-primary-foreground rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
                <CameraIcon className="w-5 h-5" />
                Capture
              </div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCapture}
                className="hidden"
              />
            </label>

            {/* Paste an image copied from any website. */}
            <button
              type="button"
              onClick={pasteFromClipboardButton}
              className="w-full py-3 bg-secondary text-foreground rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
            >
              <ClipboardPaste className="w-4 h-4" />
              Paste image
            </button>
            <p className="text-xs text-muted-foreground text-center">
              Or copy an image on any site and press Ctrl/Cmd+V here.
            </p>
            {pasteHint && (
              <p className="text-xs text-muted-foreground text-center">{pasteHint}</p>
            )}

            {/* Bulk-add straight from the phone's photo library: detect every
                garment across many photos and turn each into a clean cutout. */}
            <div className="pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => navigate('/wardrobe/import')}
                className="w-full py-3 bg-secondary text-foreground rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
              >
                <Images className="w-4 h-4" />
                Import from camera roll
              </button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Pick many photos at once — we detect each garment and cut it out.
              </p>
            </div>
          </div>
        )}

        {/* DETECTING */}
        {stage === 'detecting' && (
          <div className="space-y-6">
            <div className="aspect-[3/4] bg-secondary rounded-2xl flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 animate-pulse">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm font-medium text-foreground">Analyzing image…</p>
              <p className="text-xs text-muted-foreground mt-1">Detecting clothing</p>
            </div>
          </div>
        )}

        {/* CHOOSE TYPE */}
        {stage === 'choose-type' && (
          <div className="space-y-6">
            {image && (
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-secondary">
                <img src={image} alt="Captured" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">AI detected this as</p>
              <p className="text-xl font-bold text-foreground">
                {detectedType === 'fit' ? 'An Outfit (Fit)' : 'A Single Item'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <TypeCard
                icon={<Shirt className="w-8 h-8" />}
                title="Item"
                subtitle="Single piece"
                active={selectedType === 'item'}
                suggested={detectedType === 'item'}
                onClick={() => handleConfirmType('item')}
              />
              <TypeCard
                icon={<User className="w-8 h-8" />}
                title="Fit"
                subtitle="Full outfit"
                active={selectedType === 'fit'}
                suggested={detectedType === 'fit'}
                onClick={() => handleConfirmType('fit')}
              />
            </div>
            <Button variant="secondary" fullWidth onClick={resetAll}>
              <RotateCcw className="w-4 h-4" />
              Retake Photo
            </Button>
          </div>
        )}

        {/* CHOOSE MODE */}
        {stage === 'choose-mode' && (
          <div className="space-y-6">
            <div className="text-center py-2">
              <p className="text-sm text-muted-foreground mb-1">Adding a new</p>
              <p className="text-xl font-bold text-foreground">{selectedType === 'fit' ? 'Outfit' : 'Item'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleChooseMode('ai')}
                className="p-6 rounded-2xl border-2 border-primary bg-primary/5 transition-all flex flex-col items-center gap-3"
              >
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-foreground">Use AI</p>
                  <p className="text-xs text-muted-foreground">Auto-detect details</p>
                </div>
                <span className="text-xs text-primary font-medium">Recommended</span>
              </button>
              <button
                onClick={() => handleChooseMode('manual')}
                className="p-6 rounded-2xl border-2 border-border bg-card hover:border-primary/30 transition-all flex flex-col items-center gap-3"
              >
                <div className="w-16 h-16 rounded-xl bg-secondary flex items-center justify-center">
                  <Hand className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-foreground">Manual</p>
                  <p className="text-xs text-muted-foreground">Enter details yourself</p>
                </div>
              </button>
            </div>
            <button
              onClick={() => setStage('choose-type')}
              className="w-full py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to type selection
            </button>
          </div>
        )}

        {/* AI PROCESSING */}
        {stage === 'ai-processing' && (
          <div className="space-y-6">
            <div className="aspect-square bg-secondary rounded-2xl flex flex-col items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-10 h-10 text-primary animate-pulse" />
              </div>
              <p className="text-lg font-semibold text-foreground mb-2">AI is analyzing…</p>
              <p className="text-sm text-muted-foreground text-center px-8">
                {selectedType === 'fit' ? 'Matching items from your wardrobe' : 'Detecting item details'}
              </p>
            </div>
          </div>
        )}

        {/* ITEM RESULTS */}
        {stage === 'item-results' && analysis && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">AI Analysis</h2>
              <button onClick={resetAll} className="text-sm text-muted-foreground hover:text-foreground">
                Start Over
              </button>
            </div>

            {image && (
              <div className="aspect-video rounded-xl overflow-hidden bg-secondary">
                <img src={image} alt="Captured item" className="w-full h-full object-cover" />
              </div>
            )}

            <div className="space-y-3">
              {ITEM_FIELDS.map((key) => {
                const field = analysis[key] as AiFieldResult;
                if (!field) return null;
                return (
                  <div key={key} className="bg-card rounded-xl border border-border p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {FIELD_LABELS[key] ?? key}
                      </span>
                      <span
                        className={cn(
                          'text-xs font-medium px-2 py-0.5 rounded-full',
                          confidenceClass(field.confidence),
                        )}
                      >
                        {Math.round(field.confidence * 100)}% confident
                      </span>
                    </div>
                    {key === 'brand' ? (
                      <ComboSelect
                        value={fieldValues.brand ?? ''}
                        options={designerNames}
                        onChange={(val) => setFieldValues((v) => ({ ...v, brand: val }))}
                        placeholder="Select a designer"
                        addLabel="+ Add new designer…"
                        addPlaceholder="e.g., Loro Piana"
                      />
                    ) : (
                      <input
                        type="text"
                        value={fieldValues[key] ?? ''}
                        onChange={(e) => setFieldValues((v) => ({ ...v, [key]: e.target.value }))}
                        className={inputClass}
                      />
                    )}
                    {field.alternatives && field.alternatives.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {field.alternatives.map((alt) => (
                          <button
                            key={alt}
                            type="button"
                            onClick={() => setFieldValues((v) => ({ ...v, [key]: alt }))}
                            className={cn(
                              'text-xs px-2 py-1 rounded-lg transition-colors',
                              fieldValues[key] === alt
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary text-muted-foreground hover:text-foreground',
                            )}
                          >
                            {alt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Store + size aren't AI-detected — filling them here skips a trip
                to the full form. */}
            <div className="space-y-4">
              <Field label="Store">
                <ComboSelect
                  value={storeName}
                  options={storeNames}
                  onChange={(v) => {
                    setStoreName(v);
                    setStoreId(stores.find((st) => st.name === v)?.id ?? '');
                  }}
                  placeholder="Where you bought it"
                  addLabel="+ Add new store…"
                  addPlaceholder="Store name"
                />
              </Field>
              <SizeField
                category={fieldValues.category ?? ''}
                system={sizeSystem}
                size={size}
                isBespoke={isBespoke}
                bespokeDate={bespokeDate}
                acquisitionDate={todayISO()}
                onSystemChange={setSizeSystem}
                onSizeChange={setSize}
                onBespokeChange={setIsBespoke}
                onBespokeDateChange={setBespokeDate}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                fullWidth
                onClick={() =>
                  goToAddItem({
                    name: fieldValues.name,
                    brand: fieldValues.brand,
                    category: fieldValues.category,
                    color: fieldValues.color,
                    material: fieldValues.material,
                    season: fieldValues.season,
                  })
                }
                disabled={savingItem}
              >
                Add More Info
              </Button>
              <Button fullWidth onClick={handleAddItem} disabled={savingItem}>
                {savingItem ? 'Saving…' : 'Add to Wardrobe'}
              </Button>
            </div>
          </div>
        )}

        {/* FIT RESULTS */}
        {stage === 'fit-results' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Match Items</h2>
              <button onClick={resetAll} className="text-sm text-muted-foreground hover:text-foreground">
                Start Over
              </button>
            </div>

            {image && (
              <div className="aspect-video rounded-xl overflow-hidden bg-secondary">
                <img src={image} alt="Captured outfit" className="w-full h-full object-cover" />
              </div>
            )}

            {garments.length === 0 ? (
              <div className="text-center py-8 bg-secondary/50 rounded-xl">
                <p className="text-sm text-muted-foreground">No garments detected in this photo.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {garments.map((garment, idx) => (
                  <div key={idx} className="bg-card rounded-2xl border border-border p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-foreground">{garment.description}</p>
                      {garment.matchedItemId ? (
                        <span
                          className={cn(
                            'text-xs font-medium px-2 py-0.5 rounded-full',
                            confidenceClass(garment.confidence),
                          )}
                        >
                          {Math.round(garment.confidence * 100)}% match
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                          None selected
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {garment.candidates.map((cand) => {
                        const item = itemsById[cand.itemId];
                        const selected = garment.matchedItemId === cand.itemId;
                        return (
                          <button
                            key={cand.itemId}
                            type="button"
                            onClick={() => toggleGarmentMatch(idx, cand.itemId)}
                            className={cn(
                              'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-colors',
                              selected
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary text-foreground hover:bg-secondary/70',
                            )}
                          >
                            {selected && <Check className="w-3 h-3" />}
                            {item?.name ?? 'Item'}
                            <span className={selected ? 'opacity-80' : 'text-muted-foreground'}>
                              {Math.round(cand.confidence * 100)}%
                            </span>
                          </button>
                        );
                      })}
                      {garment.candidates.length === 0 && (
                        <span className="text-xs text-muted-foreground">No candidates found</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Button
              fullWidth
              onClick={startLogFit}
              disabled={matchedItemIds.length === 0 || loggingFit}
            >
              {loggingFit ? 'Checking…' : `Log Today's Wear (${matchedItemIds.length})`}
            </Button>
          </div>
        )}

        {/* FIT LOGGING — name a new fit */}
        {stage === 'fit-logging' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Name This Fit</h2>
              <button
                onClick={() => setStage('fit-results')}
                className="p-1.5 rounded-full hover:bg-secondary"
                aria-label="Back"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              No existing outfit matched these {matchedItemIds.length} items. Give it a name to save and log
              today's wear.
            </p>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Fit Name *</label>
              <input
                type="text"
                value={fitName}
                onChange={(e) => setFitName(e.target.value)}
                placeholder="e.g., Friday Office Look"
                className={inputClass}
                autoFocus
              />
            </div>
            <Button fullWidth onClick={createFitAndLog} disabled={!fitName.trim() || loggingFit}>
              {loggingFit ? 'Saving…' : 'Create Fit & Log Wear'}
            </Button>
          </div>
        )}

        {/* FIT SUCCESS */}
        {stage === 'fit-success' && (
          <div className="space-y-6 text-center py-10">
            <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground mb-1">Wear Logged!</p>
              <p className="text-sm text-muted-foreground">
                {existingFit ? `Logged for "${existingFit.name}".` : 'Your new fit was saved and logged.'}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" fullWidth onClick={resetAll}>
                Capture Another
              </Button>
              <Button fullWidth onClick={() => navigate('/fits')}>
                View Fits
              </Button>
            </div>
          </div>
        )}
      </div>

      {(stage === 'detecting' || stage === 'ai-processing') && (
        <div className="sr-only" aria-live="polite">
          <Spinner />
        </div>
      )}
    </Page>
  );
}

function TypeCard({
  icon,
  title,
  subtitle,
  active,
  suggested,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  active: boolean;
  suggested: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'p-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-3',
        active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/30',
      )}
    >
      <div className="w-16 h-16 rounded-xl bg-secondary flex items-center justify-center text-muted-foreground">
        {icon}
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      {suggested && <span className="text-xs text-primary font-medium">AI Suggested</span>}
    </button>
  );
}
